import React, { createContext, useContext, useState, useEffect } from 'react'
import apiService from '../services/api'

const AuthContext = createContext()

// Chatwoot URL for redirects
const CHATWOOT_URL = import.meta.env.VITE_CHATWOOT_URL || 'https://crm.godreamx.com'

// Check if running in local development mode
const IS_LOCAL_DEV = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
)

export { AuthContext }

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // One-shot, local-only signal for "this page load is the Chatwoot Create
  // Website redirect" — unlike activeCrmDeal.prefillPending (which is
  // server-persisted and keeps reappearing on ordinary logins), this is
  // never true unless the URL actually carried a fresh ?jwt= this load.
  // NOTE: this is true for EVERY ?jwt= arrival, deal-linked or not — it
  // does not by itself mean a deal is involved. Use crmDealRedirect below
  // for that.
  const [arrivedViaCrmRedirect, setArrivedViaCrmRedirect] = useState(false)
  // True only when THIS SPECIFIC redirect's URL carried &source=deal --
  // set by templatebuilderbackend's GET /api/auth/auto-login only when its
  // own deal_id query param was present (routes/auth.js). Unlike
  // activeCrmDeal.prefillPending (server-persisted, can go stale across
  // later plain logins), this is derived fresh from the current URL only,
  // so a plain sidebar login can never be mistaken for a deal-linked one
  // just because an earlier deal left prefillPending stuck at true.
  const [crmDealRedirect, setCrmDealRedirect] = useState(false)

  const isAuthenticated = !!user

  useEffect(() => {
    // Check for JWT token in URL (from auto-login redirect)
    const urlParams = new URLSearchParams(window.location.search)
    const jwtToken = urlParams.get('jwt')

    if (jwtToken) {
      console.log('🔐 Auto-login with JWT from Chatwoot')
      setArrivedViaCrmRedirect(true)
      setCrmDealRedirect(urlParams.get('source') === 'deal')
      handleAutoLogin(jwtToken)
      return
    }

    // Check for existing auth on app load
    const token = localStorage.getItem('jwt')

    if (token) {
      // Verify token is still valid by fetching user profile
      fetchUserProfile()
    } else {
      // No token - redirect to Chatwoot
      setLoading(false)
    }
  }, [])

  const handleAutoLogin = async (jwtToken) => {
    try {
      // Store the token
      localStorage.setItem('jwt', jwtToken)

      // Fetch user profile to validate token and get user data
      await fetchUserProfile()

      // Clean URL
      const newUrl = window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    } catch (error) {
      console.error('Auto-login failed:', error)
      localStorage.removeItem('jwt')
      setLoading(false)
    }
  }

  const fetchUserProfile = async () => {
    try {
      const data = await apiService.getUserProfile()
      if (data.success) {
        const userData = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          avatar: data.user.avatar,
          org: data.user.org,
          orgId: data.user.orgId,
          plan: data.user.plan,
          subdomain: data.user.subdomain,
          role: data.user.role,
          isEmailVerified: data.user.isEmailVerified,
          lastLogin: data.user.lastLogin,
          createdAt: data.user.createdAt,
          activeCrmDeal: data.user.activeCrmDeal
        }
        setUser(userData)
        localStorage.setItem('email', data.user.email)
        localStorage.setItem('org', data.user.org)
      } else {
        // Invalid token
        logout()
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      // Clear invalid auth data
      logout()
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (firstName, lastName) => {
    try {
      const data = await apiService.updateProfile(firstName, lastName)
      if (data.success) {
        setUser(prev => ({
          ...prev,
          firstName: data.user.firstName,
          lastName: data.user.lastName
        }))
        return { success: true, message: data.message }
      }
      return { success: false, error: data.error }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const data = await apiService.changePassword(currentPassword, newPassword)
      return { success: data.success, message: data.message, error: data.error }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      localStorage.removeItem('jwt')
      localStorage.removeItem('email')
      localStorage.removeItem('org')

      // Don't redirect on localhost - just reload
      if (IS_LOCAL_DEV) {
        console.log('🔧 Local dev: Logged out. Set token manually to login again.')
        window.location.reload()
      } else {
        // Redirect to Chatwoot after logout
        window.location.href = CHATWOOT_URL
      }
    }
  }

  // Send the user through Chatwoot's own SSO bridge rather than its bare
  // login page. If they're already signed into Chatwoot, this completes
  // immediately with no extra click. If not, the bridge itself now redirects
  // to Chatwoot's login with a return path back to itself, so login still
  // ends here automatically instead of stranding the user in the CRM
  // dashboard with no way back to what they were doing.
  const redirectToLogin = () => {
    if (IS_LOCAL_DEV) {
      console.log('🔧 Local dev: Would redirect to login. Set JWT token manually instead.')
      console.log('Run: localStorage.setItem("jwt", "<your-token>"); then refresh')
      return
    }
    window.location.href = `${CHATWOOT_URL}/api/v1/template_builder_auto_login`
  }

  const value = {
    user,
    isAuthenticated,
    loading,
    arrivedViaCrmRedirect,
    crmDealRedirect,
    logout,
    fetchUserProfile,
    updateProfile,
    changePassword,
    redirectToLogin,
    CHATWOOT_URL
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
